import { ArrowLeft, Printer, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { API, errMsg, formatDate, money } from "@/lib/api";
import { StatusBadge } from "./Dashboard";

export default function AdminOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => API.get(`/admin/orders/${id}`).then((r) => setOrder(r.data)).catch((e) => toast.error(errMsg(e)));

  useEffect(() => {
    load();
    API.get("/admin/settings").then((r) => setStatuses(r.data.order_statuses || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const setStatus = async (status) => {
    setSaving(true);
    try {
      const r = await API.patch(`/admin/orders/${id}`, { status });
      setOrder(r.data);
      toast.success(status === "Completed" ? "Order completed — invoice emailed to the customer" : `Status updated to ${status}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      const r = await API.patch(`/admin/orders/${id}`, { note: note.trim() });
      setOrder(r.data);
      setNote("");
      toast.success("Note added");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const advance = async () => {
    setSaving(true);
    try {
      const r = await API.post(`/admin/orders/${id}/advance`);
      setOrder(r.data);
      toast.success(r.data.status === "Completed" ? "Order completed — invoice emailed to the customer" : `Order moved to ${r.data.status}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const flow = statuses.filter((s) => s !== "Cancelled");
  const idx = flow.indexOf(order?.status);
  const nextStatus = order && idx >= 0 && idx < flow.length - 1 ? flow[idx + 1] : null;

  const remove = async () => {
    try {
      await API.delete(`/admin/orders/${id}`);
      toast.success("Order deleted");
      navigate("/admin/orders");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!order) return <Skeleton className="h-96 rounded-xl" />;

  return (
    <div data-testid="admin-order-detail">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/admin/orders")} data-testid="order-back-btn" aria-label="Back"><ArrowLeft className="h-5 w-5" /></Button>
        <h1 className="font-display text-3xl tracking-tight" data-testid="order-number-heading">{order.order_number}</h1>
        <StatusBadge status={order.status} />
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="order-print-btn"><Printer className="mr-2 h-4 w-4" /> Print</Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-destructive" data-testid="order-delete-btn"><Trash2 className="mr-2 h-4 w-4" /> Delete</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this order?</AlertDialogTitle>
                <AlertDialogDescription>This permanently removes order {order.order_number}. To keep a record, change its status to Cancelled instead.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="order-delete-cancel">Keep order</AlertDialogCancel>
                <AlertDialogAction onClick={remove} data-testid="order-delete-confirm">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card data-testid="order-items-card">
            <CardContent className="p-6">
              <h2 className="font-display text-xl mb-4">Items</h2>
              <div className="space-y-3">
                {order.items.map((it, i) => (
                  <div key={i} className="flex justify-between gap-4 text-sm border-b pb-3 last:border-0">
                    <div>
                      <p className="font-medium">{it.name} × {it.quantity}</p>
                      {it.variation && <p className="text-muted-foreground">{it.variation} · {money(it.unit_price)}</p>}
                      {(it.addons || []).map((a) => <p key={a.name} className="text-xs text-muted-foreground">+ {a.name} ({money(a.price)})</p>)}
                    </div>
                    <span className="font-semibold">{money(it.line_total)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t mt-4 pt-4 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{money(order.subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Delivery</span><span>{money(order.delivery_fee)}</span></div>
                <div className="flex justify-between font-semibold text-base"><span>Total</span><span data-testid="order-total">{money(order.total)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Payment</span><span data-testid="order-payment-status">{order.payment_status} ({order.payment_method})</span></div>
              </div>
            </CardContent>
          </Card>

          {order.note && (
            <Card data-testid="order-note-card">
              <CardContent className="p-6">
                <h2 className="font-display text-xl mb-2">Customer message</h2>
                <p className="text-sm text-muted-foreground whitespace-pre-line">{order.note}</p>
              </CardContent>
            </Card>
          )}

          <Card data-testid="order-internal-notes-card">
            <CardContent className="p-6">
              <h2 className="font-display text-xl mb-4">Internal notes</h2>
              {(order.internal_notes || []).length === 0 && <p className="text-sm text-muted-foreground mb-4">No notes yet.</p>}
              <div className="space-y-3 mb-4">
                {(order.internal_notes || []).map((n, i) => (
                  <div key={i} className="text-sm border-l-2 border-primary/40 pl-3" data-testid={`internal-note-${i}`}>
                    <p>{n.text}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{formatDate(n.at)}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input placeholder="Add an internal note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="internal-note-input" />
                <Button onClick={addNote} disabled={!note.trim()} data-testid="add-note-btn">Add</Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card data-testid="order-status-card">
            <CardContent className="p-6">
              <h2 className="font-display text-xl mb-4">Status</h2>
              {nextStatus ? (
                <Button className="w-full rounded-full" size="lg" onClick={advance} disabled={saving} data-testid="order-advance-btn">
                  Mark as {nextStatus} →
                </Button>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="order-final-status-note">
                  This order is {order.status}.
                </p>
              )}
              {order.status !== "Cancelled" && order.status !== "Completed" && (
                <Button variant="outline" className="w-full rounded-full mt-2 text-destructive" onClick={() => setStatus("Cancelled")} disabled={saving} data-testid="order-cancel-btn">
                  Cancel order
                </Button>
              )}
              <div className="mt-5 pt-4 border-t">
                <p className="text-xs text-muted-foreground mb-2">Set a specific status:</p>
                <Select value={order.status} onValueChange={setStatus} disabled={saving}>
                  <SelectTrigger data-testid="order-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {statuses.map((s) => <SelectItem key={s} value={s} data-testid={`status-option-${s.replace(/[\s/]+/g, "-").toLowerCase()}`}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground mt-3">Completing an order emails an invoice to the customer.</p>
              <p className="text-xs text-muted-foreground mt-2">Placed {formatDate(order.created_at)}</p>
            </CardContent>
          </Card>

          <Card data-testid="order-customer-card">
            <CardContent className="p-6">
              <h2 className="font-display text-xl mb-4">Customer</h2>
              <div className="text-sm space-y-1.5">
                <p className="font-medium">{order.customer?.name}</p>
                <p><a href={`mailto:${order.customer?.email}`} className="text-primary hover:underline">{order.customer?.email}</a></p>
                <p><a href={`tel:${order.customer?.phone}`} className="text-primary hover:underline">{order.customer?.phone}</a></p>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="order-delivery-card">
            <CardContent className="p-6">
              <h2 className="font-display text-xl mb-4">Delivery</h2>
              <div className="text-sm space-y-1.5">
                <p className="font-medium">{order.delivery?.name || order.customer?.name}</p>
                <p className="text-muted-foreground whitespace-pre-line">{order.delivery?.address}</p>
                <p className="text-muted-foreground">{order.delivery?.postcode}</p>
                {order.delivery?.date && <p>Requested date: <strong>{order.delivery.date}</strong></p>}
                {order.delivery?.instructions && <p className="text-muted-foreground italic">“{order.delivery.instructions}”</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
